import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { preloadDotLottie } from "@/lib/loadDotLottie";
import { DotLottieCanvas } from "@/components/DotLottieCanvas";
import type { HomeCta, HomeHeroData } from "@/lib/homepage";

const DEFAULT_HERO_TEXT = {
  heading: "Unleash the full potential of Currency Orchestration with OnPlexus",
  subheading:
    "Start with our Proprietary MoEX FX Module to optimize your Currency purchasing. Then scale into our different products and services and orchestrate your Currency in innovative ways.",
} as const;

type HeroProps = {
  data: HomeHeroData;
};

function isExternalUrl(url: string) {
  return /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");
}

function CtaButton({
  cta,
  variant,
}: {
  cta?: HomeCta | null;
  variant: "primary" | "secondary";
}) {
  if (!cta) return null;

  const common = variant === "primary"
    ? "bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl px-6 py-3 h-12 font-semibold text-lg"
    : "border-2 border-onplexus-gold text-onplexus-gold hover:bg-onplexus-gold-bright hover:text-onplexus-dark bg-transparent rounded-xl px-6 py-3 h-12 font-semibold text-lg";

  const children = (
    <>
      {cta.label}
      {cta.showIcon !== false && (
        <span className="onplexus-icon text-5xl pt-0.5">→</span>
      )}
    </>
  );

  if (isExternalUrl(cta.url)) {
    return (
      <Button asChild variant={variant === "primary" ? "default" : "outline"} className={common}>
        <a href={cta.url}>{children}</a>
      </Button>
    );
  }

  return (
    <Button asChild variant={variant === "primary" ? "default" : "outline"} className={common}>
      <Link to={cta.url}>{children}</Link>
    </Button>
  );
}

export default function Hero({ data }: HeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldRenderAnimation, setShouldRenderAnimation] = useState(false);
  const [allowAnimation, setAllowAnimation] = useState(false);
  const canAnimate = data.showAnimation && Boolean(data.lottieSrc);

  const heading = data.heading || DEFAULT_HERO_TEXT.heading;
  const subheading = data.subheading || DEFAULT_HERO_TEXT.subheading;

  useEffect(() => {
    if (!canAnimate || typeof window === "undefined") {
      setAllowAnimation(false);
      return;
    }

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const prefersNoData = window.matchMedia("(prefers-reduced-data: reduce)");
    const isDesktopViewport = window.matchMedia("(min-width: 768px)");

    const evaluate = () => {
      const shouldAllow = !prefersReduced.matches && !prefersNoData.matches && isDesktopViewport.matches;
      setAllowAnimation(shouldAllow);
      if (shouldAllow) {
        preloadDotLottie();
      }
    };

    evaluate();

    const handleReducedMotion = () => evaluate();
    const handleReducedData = () => evaluate();
    const handleViewport = () => evaluate();

    if (typeof prefersReduced.addEventListener === "function") {
      prefersReduced.addEventListener("change", handleReducedMotion);
    } else {
      prefersReduced.addListener(handleReducedMotion);
    }

    if (typeof prefersNoData.addEventListener === "function") {
      prefersNoData.addEventListener("change", handleReducedData);
    } else if (typeof prefersNoData.addListener === "function") {
      prefersNoData.addListener(handleReducedData);
    }

    if (typeof isDesktopViewport.addEventListener === "function") {
      isDesktopViewport.addEventListener("change", handleViewport);
    } else {
      isDesktopViewport.addListener(handleViewport);
    }

    return () => {
      if (typeof prefersReduced.removeEventListener === "function") {
        prefersReduced.removeEventListener("change", handleReducedMotion);
      } else {
        prefersReduced.removeListener(handleReducedMotion);
      }
      if (typeof prefersNoData.removeEventListener === "function") {
        prefersNoData.removeEventListener("change", handleReducedData);
      } else if (typeof prefersNoData.removeListener === "function") {
        prefersNoData.removeListener(handleReducedData);
      }
      if (typeof isDesktopViewport.removeEventListener === "function") {
        isDesktopViewport.removeEventListener("change", handleViewport);
      } else {
        isDesktopViewport.removeListener(handleViewport);
      }
    };
  }, [canAnimate]);

  useEffect(() => {
    if (!allowAnimation || !canAnimate || typeof window === "undefined") {
      setShouldRenderAnimation(false);
      return;
    }

    if (!containerRef.current) {
      setShouldRenderAnimation(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldRenderAnimation(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -25% 0px", threshold: 0.1 },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [allowAnimation, canAnimate]);

  return (
    <section className="pt-32 pb-16 px-4 sm:px-12 md:px-6 ">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row-reverse items-start gap-6 items-center">


          {/* Hero Lottie Animation */}
          <div ref={containerRef} className="relative flex flex-1 justify-center w-full max-w-[512px]">
            {canAnimate && allowAnimation && shouldRenderAnimation ? (
              <DotLottieCanvas
                src={data.lottieSrc!}
                width={512}
                height={512}
                className="max-h-[300px] max-w-[300px] sm:max-h-[400px] sm:max-w-[400px] md:max-h-[320px] md:max-w-[320px] lg:max-h-[512px] lg:max-w-[512px]"
                aria-label={data.lottieAlt ?? undefined}
              />
            ) : (
              <div className="h-[320px] w-[320px] rounded-full bg-gradient-to-br from-onplexus-gold/20 via-onplexus-gold/5 to-transparent blur-3xl" />
            )}
          </div>


          {/* Content */}
          <div className="flex flex-col grow flex-1 space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-normal text-white leading-tight">
                {heading}
              </h1>
              <p className="text-xl text-white/70 leading-relaxed">
                {subheading}
              </p>
            </div>
  
            <div className="flex flex-col sm:flex-row gap-6">
              <CtaButton cta={data.primaryCta} variant="primary" />
              <CtaButton cta={data.secondaryCta} variant="secondary" />
            </div>
          </div>
          

        </div>
      </div>
    </section>
  );
}
